import React, { useEffect, useMemo } from 'react';
import { Tab, Tabs, Box, Divider, Link } from '@mui/material';
import StyledMain from '../dashboard/styled/StyledMain';
import ProfileTab from './components/ProfileTab';
import DefaultTab from './components/DefaultsTab';
import Notifications from './components/NotificationsTab';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'src/store/store';
import BillingTab from './components/BillingTab';
import { Logout } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetCurrentUser } from 'src/features/user/userSlice';
import CompanyTab from './components/CompanyTab';
import { Typography } from 'src/components/new/Typography';
import { resetAdminState } from 'src/features/admin/adminSlice';
import { resetCompanyState } from 'src/features/company/companySlice';
import { logoutUser } from 'src/features/user/utils';

export default function SettingsPage() {
  const currentUser = useSelector((state: RootState) => state.user.currentUser);

  const availableTabs = [
    { label: 'Profile', component: <ProfileTab currentUser={currentUser} />, queryParam: 'profile' },
    ...(currentUser.role === 'admin' ? [{ label: 'Company', component: <CompanyTab />, queryParam: 'company' }] : []),
    { label: 'Defaults', component: <DefaultTab currentUser={currentUser} />, queryParam: 'defaults' },
    ...(currentUser.role === 'admin'
      ? [{ label: 'Billing', component: <BillingTab currentUser={currentUser} />, queryParam: 'billing' }]
      : []),
    { label: 'Notifications', component: <Notifications />, queryParam: 'notifications' },
  ];

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const tab = searchParams.get('tab');
    const isValid = availableTabs.some((t) => t.queryParam === tab);

    if (!tab || !isValid) {
      setSearchParams({ tab: availableTabs[0].queryParam }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const activeTab = searchParams.get('tab') || availableTabs[0].queryParam;

  const activeTabIndex = useMemo(() => {
    const index = availableTabs.findIndex((tab) => tab.queryParam === activeTab);
    return index === -1 ? 0 : index;
  }, [activeTab]);

  const handleTabChange = (event: any, newValue: any) => {
    setSearchParams({ tab: availableTabs[newValue].queryParam });
  };

  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const signOut = () => {
    dispatch(logoutUser());
    dispatch(resetCurrentUser());
    dispatch(resetAdminState());
    dispatch(resetCompanyState());
    navigate('/auth/sign-in');
  };

  return (
    <Box display="flex" flexDirection="row">
      <StyledMain open={true}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem',
          }}
        >
          <Typography variant="h4" sx={{ fontSize: '3.25rem', fontFamily: 'ABC Arizona Mix', fontWeight: 300, mb: 2, mt: 1 }}>
            Settings
          </Typography>
          <Link
            onClick={() => signOut()}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '0.3125rem',
              cursor: 'pointer',
              color: '#273194',
              textDecoration: 'none',
            }}
          >
            <Logout sx={{ fontSize: '1.25rem', color: '#436EB2' }} />
            <Typography sx={{ fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 600, color: '#436EB2', mr: 8 }}>
              Log Out
            </Typography>
          </Link>
        </Box>
        <Box sx={{ width: '100%' }}>
          <Tabs
            value={activeTabIndex}
            onChange={handleTabChange}
            TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
            sx={{
              padding: '0 2.25rem',
              minHeight: '3rem',
              '& .MuiTabs-indicator': {
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: 'transparent',
              },
              '& .MuiTabs-indicatorSpan': {
                maxWidth: 40,
                width: '100%',
                backgroundColor: '#436EB2',
                borderRadius: '6.25rem 6.25rem 0 0',
              },
            }}
          >
            {availableTabs.map((tab) => (
              <Tab
                key={`tab_${tab.label}`}
                label={tab.label}
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Instrument Sans',
                  fontSize: '1rem',
                  lineHeight: '1.25rem',
                  minHeight: '3rem',
                  textAlign: 'center',
                  color: '#45464F',
                  '&.Mui-selected': {
                    color: '#436EB2',
                  },
                }}
              />
            ))}
          </Tabs>
          <Divider />
          <Box>{availableTabs[activeTabIndex]?.component || <Typography>No content available</Typography>}</Box>
        </Box>
      </StyledMain>
    </Box>
  );
}
